import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from 'src/components/utilities/utils';
import Loader from 'src/components/loader';
import { useAnalyticsActions } from 'src/store/analytics/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';

const QuizAttemptDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const analyticsAction = useAnalyticsActions(dispatch);

    const params = useParams<{ type: string; courseId: string; memberId: string; memberName: string; quizId: string }>();
    const type = (params.type as 'course' | 'video') ?? 'course';
    const courseId = params.courseId ?? '';
    const memberId = params.memberId ?? '';
    const quizId = params.quizId ?? '';

    const [selectedAttemptKey, setSelectedAttemptKey] = useState<string | null>(null);
    const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { quizAttempt, quizAnswers, isQuizAttemptDeleted, loading } = useSelector((state: RootState) => ({
        quizAttempt: state.analytics.quizAttempt,
        quizAnswers: state.analytics.quizAnswers,
        isQuizAttemptDeleted: state.analytics.isQuizAttemptDeleted,
        loading: state.analytics.loading
    }));

    const attempts: any[] = Array.isArray(quizAttempt) ? quizAttempt : [];
    const makeKey = (a: any) => `${a.quizresultid}__${a.attempt}`;
    const selectedAttempt = attempts.find((a: any) => makeKey(a) === selectedAttemptKey) ?? null;

    useEffect(() => {
        analyticsAction.getQuizAttemptRequest({ courseId, memberId, quizId, type });
    }, []);


    useEffect(() => {
        if (attempts && attempts.length > 0 && !selectedAttemptKey) {
            const first = attempts[0];
            setSelectedAttemptKey(makeKey(first));
            setIsLoadingAnswers(true);
            analyticsAction.getQuizAnswersRequest({
                quizResultId: first.quizresultid,
                attempt: first.attempt
            });
        }
    }, [attempts, selectedAttemptKey]);

    useEffect(() => {
        if (quizAnswers !== undefined) setIsLoadingAnswers(false);
    }, [quizAnswers]);

    useEffect(() => {
        if (isQuizAttemptDeleted) {
            setDeleteSuccess('Attempt deleted successfully.');
            setDeleteConfirmId(null);
            setIsDeleting(false);
            setSelectedAttemptKey(null);
            analyticsAction.resetAnalyticsStateRequest()
            analyticsAction.getQuizAttemptRequest({ courseId, memberId, quizId, type });
        }
    }, [isQuizAttemptDeleted]);
    const handleSelectAttempt = (attempt: any) => {
        const key = makeKey(attempt);
        if (key === selectedAttemptKey) return;
        setSelectedAttemptKey(key);
        setIsLoadingAnswers(true);
        analyticsAction.getQuizAnswersRequest({
            quizResultId: attempt.quizresultid,
            attempt: attempt.attempt
        });
    };


    const handleDeleteAttempt = (attemptKey: string) => {
        const attemptToDelete = attempts.find((a: any) => makeKey(a) === attemptKey);
        if (!attemptToDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        setDeleteSuccess(null);
        analyticsAction.deleteQuizAttemptRequest({
            quizResultId: attemptToDelete.quizresultid,
            attempt: attemptToDelete.attempt
        });
    };

    const getStatusClass = (passed: boolean) =>
        passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

    const quizName = attempts[0]?.quizname ?? 'Quiz Name';

    if (!quizAttempt) {
        return <div className="flex justify-center py-20"><Loader /></div>;
    }

    return (
        <div className="p-6 min-h-screen bg-gray-50">

            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    {/* <button
                        onClick={() => { navigate(-1), analyticsAction.resetAnalyticsStateRequest() }}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 transition-colors">
                        <i className="fa fa-arrow-left text-sm"></i>
                    </button> */}
                    <div>
                        <h1 className="text-xl font-semibold text-gray-800">{quizName}</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Quiz Attempts</p>
                    </div>
                </div>
            </div>


            {deleteSuccess && (
                <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                    <i className="fa fa-check-circle"></i>
                    <span>{deleteSuccess}</span>
                    <button className="ml-auto text-green-500 hover:text-green-700" onClick={() => setDeleteSuccess(null)}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            )}
            {deleteError && (
                <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                    <i className="fa fa-exclamation-circle"></i>
                    <span>{deleteError}</span>
                    <button className="ml-auto text-red-400 hover:text-red-600" onClick={() => setDeleteError(null)}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            )}


            {loading ? (
                <div className="text-center py-20 text-gray-500 text-sm">
                    <Loader />
                </div>
            ) : attempts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <i className="fa fa-clipboard-list text-3xl text-gray-300 mb-3"></i>
                    <p className="text-sm font-medium text-gray-500">No attempts found</p>
                    <p className="text-xs text-gray-400 mt-1">
                        {`This user hasn't attempted this quiz yet.`}
                    </p>
                </div>
            ) : (
                <div className="flex gap-5 items-start">


                    <div className="w-56 flex-shrink-0">
                        <h3 className="text-xs font-semibold uppercase text-gray-400 tracking-wide mb-3">
                            All Attempts
                        </h3>
                        <div className="flex flex-col gap-2">
                            {attempts.map((attempt: any) => {
                                const key = makeKey(attempt);
                                return (
                                    <button
                                        key={key}
                                        onClick={() => handleSelectAttempt(attempt)}
                                        className={`text-left rounded-lg px-3 py-3 border transition-all ${selectedAttemptKey === key
                                            ? 'border-purple-400 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-semibold text-gray-700">
                                                Attempt #{attempt.attempt}
                                            </span>
                                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getStatusClass(attempt.passresult)}`}>
                                                {attempt.passresult ? 'Pass' : 'Fail'}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500">{Math.round(attempt.score)}% Score</div>
                                        <div className="text-xs text-gray-400 mt-0.5">
                                            {attempt.attempteddate ? formatDate(attempt.attempteddate) : ''}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>


                    {selectedAttempt && (
                        <div className="flex-1 min-w-0">

                            {/* Attempt Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                                <div className="dashboard-card p-3 text-center">
                                    <div className="text-xl font-bold text-gray-800">{Math.round(selectedAttempt.score)}%</div>
                                    <div className="text-xs text-gray-500 mt-1">Grade</div>
                                </div>
                                <div className="dashboard-card p-3 text-center">
                                    <div className="text-xl font-bold text-gray-800">
                                        {selectedAttempt.totalcorrectanswers} / {selectedAttempt.totalquestions}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Correct Answers</div>
                                </div>
                                <div className="dashboard-card p-3 text-center">
                                    <div className="text-xl font-bold text-gray-800">
                                        {selectedAttempt.totalpointsearned} / {selectedAttempt.totalpoints}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Points Earned</div>
                                </div>
                                <div className="dashboard-card p-3 text-center">
                                    <span className={`text-sm font-semibold px-2.5 py-1 rounded ${getStatusClass(selectedAttempt.passresult)}`}>
                                        {selectedAttempt.passresult ? 'Pass' : 'Fail'}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-2">Status</div>
                                </div>
                            </div>

                            {/* Answers */}
                            <h3 className="font-semibold text-gray-700 mb-3 text-sm">Answers</h3>
                            <div className="flex flex-col gap-2 mb-5">
                                {isLoadingAnswers ? (
                                    <div className="flex justify-center py-10"><Loader /></div>
                                ) : Array.isArray(quizAnswers) && quizAnswers.length > 0 ? (
                                    quizAnswers.map((answer: any, idx: number) => (
                                        <div
                                            key={answer.questionId ?? idx}
                                            className={`rounded-lg px-4 py-3 border-l-4 ${answer.isCorrect ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
                                                }`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-800 font-medium">{answer.questionText}</p>
                                                    <div className="flex flex-wrap gap-4 mt-2">
                                                        <div className="text-xs">
                                                            <span className="text-gray-400">Selected: </span>
                                                            <span className={`font-medium ${answer.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                                                {answer.submittedAnswerText || '—'}
                                                            </span>
                                                        </div>
                                                        {!answer.isCorrect && (
                                                            <div className="text-xs">
                                                                <span className="text-gray-400">Correct: </span>
                                                                <span className="font-medium text-green-700">{answer.correctAnswerText}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className="text-xs font-medium text-gray-500">
                                                        {answer.pointsEarned}/{answer.points} pts
                                                    </span>
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${answer.isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                                                        <i className={`fa ${answer.isCorrect ? 'fa-check' : 'fa-times'} text-white text-xs`}></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500">No answer details available.</p>
                                )}
                            </div>

                            {/* Delete Attempt */}
                            {deleteConfirmId === selectedAttemptKey ? (
                                <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-700 flex-1">
                                        Are you sure you want to delete Attempt #{selectedAttempt.attempt}? This cannot be undone.
                                    </p>
                                    <button
                                        onClick={() => handleDeleteAttempt(selectedAttemptKey!)}
                                        disabled={isDeleting}
                                        className="btn-primary w-auto mt-0 mr-0 bg-red-600 hover:bg-red-700 border-red-600 text-white text-sm px-4 py-1.5">
                                        {isDeleting
                                            ? <span><i className="fa fa-spinner fa-spin mr-1"></i> Deleting...</span>
                                            : 'Yes, Delete'
                                        }
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirmId(null)}
                                        disabled={isDeleting}
                                        className="btn-primary w-auto mt-0 borderonly-btn text-sm px-4 py-1.5">
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setDeleteConfirmId(selectedAttemptKey)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">
                                    <i className="fa fa-trash"></i>
                                    Delete Attempt
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuizAttemptDetailPage;